import Vue from 'vue';
import Vuex, { Commit, Module, StoreOptions } from 'vuex';
import _ from 'lodash';
import i18n, { i18n_t } from '@/locales/i18n';
import storage from '@/renderer/scripts/utils/storage';
import meeting from '@/renderer/scripts/utils/meeting';
import tcl from '@/renderer/scripts/utils/tcl';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import { httpUrl, CONSTANT_URL } from '@/renderer/scripts/http';
import smh from '@/renderer/scripts/smh';
import {
  IS_BROWSER,
  IS_BROWSER_IN_MOBILE,
  IS_DEV,
  IS_TEST,
  IS_PRE,
  IS_PRIVATE,
  IS_PC_IN_WIN,
  IS_PC_IN_MAC,
} from '@/common/constant/platform';
import collaborateGroup from '@/renderer/store/modules/collaborateGroup/collaborateGroup';
import user from './modules/user';
import vistor from './modules/vistor';
import org from './modules/org';
import share from './modules/share';
import update from './modules/update';
import transmission from './modules/transmission';
import fileListCopy from './modules/fileListCopy';
import drawer from './modules/drawer';
import search from './modules/search';
import remoteConfig from './modules/remoteConfig';
import getters from './getters';

Vue.use(Vuex);
type BaseGuide = {
  personalSpace: {
    steps: number[];
    done: boolean;
  };
  collaborationSpace: {
    steps: number[];
    done: boolean;
  };
  done: boolean;
};
let PREFIX = '';
if (IS_DEV) {
  PREFIX = 'dev.';
} else if (IS_TEST) {
  PREFIX = 'test.';
} else if (IS_PRE) {
  PREFIX = 'pre.';
}
export type NoviceGuidance = {
  baseGuide: BaseGuide;
  guideFinishedShow: false;
  advanceGuideFinished: number[];
  searchGuideFinished: boolean;
  didAdmin: {
    didAdminCard: number[];
    didOpenManage: boolean;
    didShowAdminGuide: boolean;
  };
  workbenchTools: {
    showElectronCard: boolean;
    showAntiRansomwareCard: boolean;
    showGuideConfirm: boolean;
  };
  collaborationSpaceGuides: {
    hideShareGuide: boolean;
  };
};
interface IndexState {
  productName: string;
  productLogoUrl: string;
  serverPrefix: string;
  serverSuffix: string;
  route: {
    query: string;
    name: string;
    params: undefined;
  };
  previousRoute: {
    query: string;
    name: string;
    params: undefined;
  };
  logExporting: boolean;
  statsIsExporting: {
    teamSpace: boolean;
    teamTraffic: boolean;
    personalTraffic: boolean;
    personalSpace: boolean;
  };
  isBrowser: boolean;
  isMobile: boolean;
  isPrivate: boolean;
  onlyPasswordLogin: boolean;
  selectOrgBeforeLogin: boolean;
  isMobileStyle: boolean;
  isChooseRole: boolean;
  isWin: boolean;
  isMac: boolean;
  isInWeMeetApp: boolean;
  isTclApp: boolean;
  allowPhoneLogin: boolean;
  protocol: string;
  orgSwitchFlag: number;
  noviceGuidance: NoviceGuidance | {};
  teamPay: {
    show: boolean;
    isNew: boolean;
  };
  showOrgList: boolean;
  paySource: string;
  showLoginingMask: boolean;
  systemUsedInfo: {
    afterLoginedTS: number;
    beforeLoginedTS: number;
    usedAfterLogined: boolean;
    systemUsedTS: number;
    usedBeforeLogined: boolean;
    systemUsed: boolean;
  };
  customDomainInfo: {
    isCustomDomain: boolean;
    icp: string;
    psb: string;
    psbNumber: string;
    title: string;
    logo: string;
  };
  defaultTitle: string; // 用来记录预览前的document.title
  announcements: any[]; // 通知通告（交大使用）
}
const options: StoreOptions<IndexState> = {
  // @ts-ignore
  modules: {
    user,
    vistor,
    org,
    share,
    update,
    transmission,
    fileListCopy,
    drawer,
    search,
    collaborateGroup,
    remoteConfig,
  } as Module<any, IndexState>,
  state: {
    productName: meeting.isTMWebview ? i18n_t('会议文件空间') : i18n_t('腾讯云企业网盘'),
    productLogoUrl: meeting.isTMWebview
      ? httpUrl.staticCDNURLMap.productWemeetLogoUrl
      : httpUrl.staticCDNURLMap.productLogoUrl,
    serverPrefix: `${httpUrl.protocol}://${PREFIX}pan.tencent`,
    serverSuffix: '.com',
    // 路由
    route: {
      name: '',
      query: '',
      params: undefined,
    },
    // 上一个路由
    previousRoute: {
      name: '',
      query: '',
      params: undefined,
    },
    logExporting: false,
    // 统计模块导出csv
    statsIsExporting: {
      teamSpace: false,
      personalSpace: false,
      teamTraffic: false,
      personalTraffic: false,
    },
    isBrowser: IS_BROWSER,
    isMobile: IS_BROWSER_IN_MOBILE,
    isPrivate: IS_PRIVATE,
    onlyPasswordLogin: ConstantConfig.onlyPasswordLogin,
    selectOrgBeforeLogin: ConstantConfig.selectOrgBeforeLogin,
    allowPhoneLogin: ConstantConfig.allowPhoneLogin,
    defaultTitle: i18n_t('腾讯云企业网盘'),
    isMobileStyle: false,
    isChooseRole: false,
    isWin: IS_PC_IN_WIN,
    isMac: IS_PC_IN_MAC,
    isInWeMeetApp: meeting.isTMWebview,
    isTclApp: tcl.isTCLWebview,
    protocol: httpUrl.protocol,
    // 切换身份时主动自增
    orgSwitchFlag: 1,
    // 由于存在新老数据，默认为true(新手）
    noviceGuidance: {},
    teamPay: {
      isNew: true,
      show: false,
    },
    showOrgList: false,
    paySource: '',
    showLoginingMask: false,
    // 展示登录页遮罩
    // 是否主动操作过，用于灯塔统计使用情况。逻辑上每天至少上报一次。
    systemUsedInfo: {
      // 登录前操作记录
      usedBeforeLogined: false,
      // 登录前操作上报的时间戳
      beforeLoginedTS: 0,
      // 登录后操作记录
      usedAfterLogined: false,
      // 登录后操作上报的时间戳
      afterLoginedTS: 0,
      // 是否使用过网盘，用于灯塔上报统计当天使用人数，任意操作算使用过
      systemUsed: false,
      // 使用操作上报的时间戳
      systemUsedTS: 0,
    },
    customDomainInfo: {
      isCustomDomain: false,
      icp: '',
      psb: '',
      psbNumber: '',
      title: '',
      logo: '',
    },
    announcements: [],
  },

  mutations: {
    setRoute(state, route) {
      state.route = route;
      storage.set(storage.StoreKey.TCD_ROUTE, state.route);
    },
    setPreviousRoute(state, previousRoute) {
      state.previousRoute = previousRoute;
      storage.set(storage.StoreKey.PREVIOUS_ROUTE, state.previousRoute);
    },
    setLogExporting(state, logExporting) {
      state.logExporting = logExporting;
    },
    setStatsIsExporting(state, statsIsExporting) {
      Object.assign(state.statsIsExporting, statsIsExporting);
    },
    setMobileStyle(state, isMobileStyle) {
      state.isMobileStyle = isMobileStyle;
    },
    setChooseRole(state, isChooseRole) {
      state.isChooseRole = isChooseRole;
    },
    setOrgSwitchFlag(state) {
      state.orgSwitchFlag += 1;
    },
    setNoviceGuidance(state, guidanceData) {
      state.noviceGuidance = guidanceData;
    },
    setTeamPay(
      state,
      option = {
        show: true,
        isNew: true,
      },
    ) {
      const _option = {
        ...state.teamPay,
        ...option,
      };
      state.teamPay = _option;
    },
    setPaySource(state, paySource = '') {
      state.paySource = paySource;
    },
    setShowOrgList(state, isShow) {
      state.showOrgList = isShow;
    },
    setLoginingMask(state, isShow) {
      state.showLoginingMask = isShow;
    },
    setSystemUsed(state, systemUsedInfo) {
      state.systemUsedInfo = {
        ...state.systemUsedInfo,
        ...systemUsedInfo,
      };
    },
    setCustomDomainInfo(state, customDomainInfo) {
      state.customDomainInfo = {
        ...state.customDomainInfo,
        ...customDomainInfo,
      };
    },
    setDefaultTitle(state, defaultTitle) {
      state.defaultTitle = defaultTitle;
    },
    setAnnouncements(state, announcements) {
      state.announcements = announcements;
    },
  },
  getters: {
    ...getters,
    isMobile: state => state.isMobile,
    isPrivate: state => state.isPrivate,
    onlyPasswordLogin: state => state.onlyPasswordLogin,
    selectOrgBeforeLogin: state => state.selectOrgBeforeLogin,
    isMobileStyle: state => state.isMobileStyle,
    isChooseRole: state => state.isChooseRole,
    isBrowser: state => state.isBrowser,
    isWin: state => state.isWin,
    isMac: state => state.isMac,
    isInWeMeetApp: state => state.isInWeMeetApp,
    allowPhoneLogin: state => state.allowPhoneLogin,
    defaultTitle: state => state.defaultTitle,
    isTclApp: state => state.isTclApp,
    protocol: state => (IS_PRIVATE ? httpUrl.protocol : state.protocol),
    logExporting: state => state.logExporting,
    statsIsExporting: state => state.statsIsExporting,
    getRoute(state) {
      if (!state.route.name) {
        const route = storage.get(storage.StoreKey.ROUTE);
        let newRoute = storage.get(storage.StoreKey.TCD_ROUTE);
        if (route && !_.isEmpty(route)) {
          // 老route复制到新route并删除老的route
          newRoute = _.clone(route);
          storage.del(storage.StoreKey.ROUTE);
        }
        newRoute && (state.route = newRoute as any);
      }
      return state.route;
    },
    previousRoute(state) {
      if (!state.previousRoute.name) {
        const previousRoute = storage.get(storage.StoreKey.PREVIOUS_ROUTE);
        previousRoute && (state.previousRoute = previousRoute as any);
      }
      return state.previousRoute;
    },
    serverPrefix: state => state.serverPrefix,
    serverSuffix: state => state.serverSuffix,
    orgSwitchFlag: state => state.orgSwitchFlag,
    noviceGuidance: state => state.noviceGuidance,
    teamPay: state => state.teamPay,
    showOrgList: state => state.showOrgList,
    showLoginingMask: state => state.showLoginingMask,
    paySource: state => state.paySource,
    systemUsedInfo: state => state.systemUsedInfo,
    customDomainInfo: state => state.customDomainInfo,
    productName: state => state.productName,
    productLogoUrl: state => state.productLogoUrl,
    announcements: state => state.announcements,
  },
  actions: {
    async getOrgCustomPortal({ commit }: { commit: Commit }) {
      try {
        const { currentDomain } = CONSTANT_URL;
        const { data } = await smh.api.getOrgCustomPortal(currentDomain);
        const customDomainInfo = {
          ...data,
          isCustomDomain: true,
        };
        commit('setCustomDomainInfo', customDomainInfo);
      } catch (err: MsgError) {
        commit('setCustomDomainInfo', {
          isCustomDomain: false,
          icp: '',
          psb: '',
          psbNumber: '',
          logo: '',
          title: '',
        });
      }
    },
    async getAnnouncements({ commit }: { commit: Commit }) {
      try {
        const { data } = await smh.api.getAnnouncements({ type: 'systemMaintenance' });
        const localeMap: Record<string, string> = {
          'zh-CN': 'zh_CN',
          'en-US': 'en_US',
        };
        const list = data.contents
          .map((item: any) => {
            const content = item.content || {};
            const locale = localeMap[i18n.locale] || 'zh_CN';
            return content[locale] || '';
          })
          .filter(Boolean);
        commit('setAnnouncements', list);
      } catch (err: MsgError) {
        commit('setAnnouncements', []);
      }
    },
  },
};
export default new Vuex.Store(options);
